import { devtools } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import type { QualityFinding } from '../lib/quality-findings';

export interface GenerationWarning {
  component: string;
  message: string;
  severity?: 'warning' | 'info';
  context?: Record<string, unknown>;
}

/**
 * The last quality analysis, whichever round trip produced it.
 *
 * `/generate` and `/validate` report the same findings in different shapes, so
 * both are normalised into this one slice — the panel should not have to know
 * which endpoint the author's last action happened to hit.
 */
export interface QualityState {
  findings: QualityFinding[];
  counts: { error: number; warning: number; info: number };
  /**
   * The document these findings were computed against.
   *
   * Every finding is a JSON Pointer plus, sometimes, an RFC 6902 patch. Both
   * are meaningless against a different file, and a tab switch leaves the panel
   * showing the previous document's findings until a new analysis lands — so
   * "Apply fix" and "Reveal" must both refuse to act when this no longer names
   * the active tab.
   */
  documentName: string;
  /** Ordering ticket; see lib/quality-sequence.ts. */
  seq: number;
  profileId?: string;
  blocked?: boolean;
  truncated?: boolean;
  /** Which round trip produced this: a full build, or a validate-only pass. */
  source: 'generate' | 'validate';
  analyzedAt: number;
  /**
   * Set only when the run policy's gate actually rejected the build. The panel
   * paints a destructive "blocked" state from this, so a transport failure must
   * never land here — it would assert a rejection that never happened, and with
   * the default gate of `none` no gate was even evaluated.
   */
  gateError?: string;
  /** The analysis could not be run (offline, rate limited, server error). */
  analysisError?: string;
}

export type OutputState = {
  name?: string; // last success => document name
  text?: string; // last success => code used to generate the document
  blob?: Blob; // last success => generated document
  renderer?: string; // last success => backend captured with the generated bytes
  globalError?: string; // last failed => global error message
  isGenerating?: boolean; // document generation in progress
  generationProgress?: {
    stage: 'parsing' | 'building' | 'rendering' | 'finalizing';
    message?: string;
  };
  generationStartedAt?: number; // Date.now() when the current build started
  cancelGeneration?: () => void; // registered by the editor; aborts the in-flight build
  cacheStatus?: 'HIT' | 'MISS' | 'UNKNOWN'; // cache hit/miss status
  cacheHitRate?: string; // cache hit rate percentage
  warnings?: GenerationWarning[] | null; // warnings from custom component processing
  quality?: QualityState | null; // last quality analysis; null until one runs
  isRendering?: boolean; // preview rendering in progress (iframe/LibreOffice)
  isPreviewStale?: boolean; // preview outdated (new blob generated but not yet rendered)
  editSequence?: number; // incremented on every Monaco keystroke (init 0)
  lastBuiltSequence?: number; // stamped when generation completes (init 0)
  editTimestamp?: number; // Date.now() of the last edit (for debounce countdown)
  hasValidationErrors?: boolean; // true when Monaco reports schema validation errors
  // ⚠️ name doesn't necessarily correspond to the global error message
};

export type OutputActions = {
  setOutput: (partialState: OutputState) => void;
  bumpEditSequence: () => void;
};

export type OutputStore = OutputState & OutputActions;

export const initOutputStore = (): OutputState => {
  return {
    name: undefined,
    text: undefined,
    blob: undefined,
    globalError: undefined,
    isGenerating: false,
    generationProgress: undefined,
    isRendering: false,
    isPreviewStale: false,
    editSequence: 0,
    lastBuiltSequence: 0,
    hasValidationErrors: false,
    quality: null,
  };
};

export const defaultInitOutputState: OutputState = {
  ...initOutputStore(),
};

export const createOutputStore = (
  initState: OutputState = defaultInitOutputState
) => {
  return createStore<OutputStore>()(
    devtools((set) => ({
      ...initState,
      setOutput: (partialState) => set({ ...partialState }),
      bumpEditSequence: () =>
        set((s) => ({
          editSequence: (s.editSequence ?? 0) + 1,
          editTimestamp: Date.now(),
        })),
    }))
  );
};
